import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type { PatientAddress } from '@/pages/main/patientAddresses/types';
import type { PatientAddressFormData } from '@/schemas/patientAddressSchema';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export async function getPatientAddressList(
  siteType: string,
  clinicNumber: string,
  patientId: number
): Promise<PatientAddress[]> {
  const res = await apiClient.get<ApiResponse<{ data: PatientAddress[] }>>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses`)
  );
  return res.data.data.data;
}

export interface PatientAddressWithDays {
  data: PatientAddress[];
  days?: { start: string; end: string };
}

export async function getPatientAddressListWithDays(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  visitDate: string
): Promise<PatientAddressWithDays> {
  const res = await apiClient.get<ApiResponse<{ data: PatientAddress[]; days?: { start: string; end: string } }>>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses`),
    { params: { visit_date: visitDate } }
  );
  return { data: res.data.data.data, days: res.data.data.days };
}

export async function getPatientAddress(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  addressId: number
): Promise<PatientAddress> {
  const res = await apiClient.get<ApiResponse<{ data: PatientAddress }>>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses/${addressId}`)
  );
  return res.data.data.data;
}

export async function createPatientAddress(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  data: PatientAddressFormData
): Promise<PatientAddress> {
  const res = await apiClient.post<ApiResponse<{ data: PatientAddress }>>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses`),
    data
  );
  return res.data.data.data;
}

export async function updatePatientAddress(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  addressId: number,
  data: PatientAddressFormData
): Promise<PatientAddress> {
  const res = await apiClient.put<ApiResponse<{ data: PatientAddress }>>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses/${addressId}`),
    data
  );
  return res.data.data.data;
}

export async function deletePatientAddress(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  addressId: number
): Promise<void> {
  await apiClient.delete(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/patient-addresses/${addressId}`)
  );
}
